/**
 * FDR Oversight — Dashboard overview.
 * Fetches from /api/fdr/dashboard and renders summary stats, risk breakdown,
 * open findings, latest screening, upcoming due items, and attestation status.
 */

import React, { useState, useEffect } from 'react';

const API = process.env.REACT_APP_API_URL;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function sumByCount(arr) {
  if (!Array.isArray(arr)) return 0;
  return arr.reduce((acc, item) => acc + (item.count || 0), 0);
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dateStr);
  due.setHours(0, 0, 0, 0);
  return Math.round((due - today) / (1000 * 60 * 60 * 24));
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  // DATE columns arrive as "YYYY-MM-DD" or UTC-midnight ISO strings; both
  // render a day early in US timezones. Pin them to local midnight.
  const v = typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}(T00:00:00(\.000)?Z)?$/.test(dateStr)
    ? `${dateStr.slice(0, 10)}T00:00:00` : dateStr;
  return new Date(v).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

const ITEM_TYPE_LABELS = {
  assessment_due:          'Annual assessment due',
  contract_expiring:       'Contract expiring',
  cap_due:                 'CAP due',
  assessment_response_due: 'Vendor response due',
  rebuttal_due:            'Management responses due',
};

// Where each vendor stands in this assessment cycle — the states walk the
// real flow, so the reader sees the whole program's motion at a glance.
const CYCLE_STATES = [
  ['not_started',        'Not started',          'bg-slate-100 text-slate-500'],
  ['preparing',          'Preparing',            'bg-slate-100 text-slate-600'],
  ['awaiting_vendor',    'Awaiting vendor',      'bg-amber-100 text-amber-700'],
  ['in_review',          'In review',            'bg-sky-100 text-sky-700'],
  ['awaiting_responses', 'Awaiting responses',   'bg-violet-100 text-violet-700'],
  ['final_issued',       'Final issued',         'bg-emerald-100 text-emerald-700'],
  ['closed',             'Closed and scored',    'bg-emerald-100 text-emerald-700'],
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatCard({ label, value, gradient, icon }) {
  return (
    <div className={`rounded-2xl p-5 text-white shadow-sm ${gradient} flex items-center gap-4 min-w-0`}>
      <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d={icon} />
        </svg>
      </div>
      <div className="min-w-0">
        <p className="text-white/70 text-xs font-medium uppercase tracking-wide truncate">{label}</p>
        <p className="text-2xl font-bold leading-tight">{value ?? '—'}</p>
      </div>
    </div>
  );
}

const RISK_CONFIG = {
  critical: { bar: 'bg-red-500',    text: 'text-red-700',    bg: 'bg-red-50',    label: 'Critical' },
  high:     { bar: 'bg-orange-500', text: 'text-orange-700', bg: 'bg-orange-50', label: 'High' },
  medium:   { bar: 'bg-amber-500',  text: 'text-amber-700',  bg: 'bg-amber-50',  label: 'Medium' },
  low:      { bar: 'bg-emerald-500',text: 'text-emerald-700',bg: 'bg-emerald-50',label: 'Low' },
};

function RiskBar({ risk_level, count, max }) {
  const cfg = RISK_CONFIG[risk_level?.toLowerCase()] || RISK_CONFIG.low;
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className={`text-xs font-semibold w-16 flex-shrink-0 ${cfg.text}`}>
        {cfg.label}
      </span>
      <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${cfg.bar}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-sm font-bold text-slate-700 w-6 text-right flex-shrink-0">{count}</span>
    </div>
  );
}

const SEVERITY_CONFIG = {
  critical:    { bg: 'bg-red-100',    text: 'text-red-700',    dot: 'bg-red-500',    label: 'Critical' },
  major:       { bg: 'bg-orange-100', text: 'text-orange-700', dot: 'bg-orange-500', label: 'Major' },
  minor:       { bg: 'bg-yellow-100', text: 'text-yellow-700', dot: 'bg-yellow-500', label: 'Minor' },
  observation: { bg: 'bg-slate-100',  text: 'text-slate-600',  dot: 'bg-slate-400',  label: 'Observation' },
};

function SeverityBadge({ severity, count }) {
  const cfg = SEVERITY_CONFIG[severity?.toLowerCase()] || SEVERITY_CONFIG.observation;
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${cfg.bg}`}>
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
      <span className={`text-xs font-semibold ${cfg.text}`}>{cfg.label}</span>
      <span className={`text-xs font-bold ${cfg.text}`}>{count}</span>
    </div>
  );
}

function DueDateCell({ due_date }) {
  const days = daysUntil(due_date);
  if (days === null) return <span className="text-slate-400 text-sm">—</span>;

  let colorClass = 'text-slate-500';
  let label = `${days}d`;

  if (days < 0) {
    colorClass = 'text-red-600 font-semibold';
    label = `${Math.abs(days)}d overdue`;
  } else if (days === 0) {
    colorClass = 'text-red-600 font-semibold';
    label = 'Today';
  } else if (days < 7) {
    colorClass = 'text-amber-600 font-semibold';
    label = `${days}d`;
  }

  return (
    <div>
      <span className={`text-sm ${colorClass}`}>{formatDate(due_date)}</span>
      <span className={`ml-2 text-xs ${colorClass}`}>({label})</span>
    </div>
  );
}

function SectionHeader({ title, onViewAll }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-xl font-bold text-slate-800">{title}</h2>
      {onViewAll && (
        <button
          onClick={onViewAll}
          className="text-sm font-semibold text-orange-600 hover:text-orange-700 transition-colors flex items-center gap-1"
        >
          View All
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-4 border-slate-200 border-t-orange-500 rounded-full animate-spin" />
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="text-center">
        <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
        </div>
        <p className="text-slate-600 text-sm">{message || 'Failed to load dashboard data.'}</p>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function FDRDashboard({ onOpenEntity, onNavigate }) {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(`${API}/api/fdr/dashboard`, { credentials: 'include' })
      .then(res => {
        if (!res.ok) throw new Error(`Request failed: ${res.status}`);
        return res.json();
      })
      .then(data => {
        setDashboard(data.dashboard || data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error)   return <ErrorState message={error} />;
  if (!dashboard) return <ErrorState message="No dashboard data returned." />;

  const {
    total_entities,
    open_findings       = [],
    open_caps           = [],
    risk_breakdown      = [],
    upcoming_due_items  = [],
    assessment_cycle    = [],
  } = dashboard;

  const totalFindings = sumByCount(open_findings);
  const totalCaps     = sumByCount(open_caps);
  const maxRiskCount  = risk_breakdown.length > 0 ? Math.max(...risk_breakdown.map(r => r.count || 0)) : 1;
  const cycleCount = (state) => assessment_cycle.find((r) => r.state === state)?.count || 0;
  const inCycle = assessment_cycle.filter((r) => !['not_started', 'closed'].includes(r.state)).reduce((s, r) => s + (r.count || 0), 0);

  return (
    <div className="p-8 space-y-6 bg-[#f8fafc] min-h-full">

      {/* 0. CMS-ready reports — wires the previously-orphaned report endpoints */}
      <section className="flex items-center justify-end gap-2 -mb-2">
        <ReportButton label="FDR Inventory Report" endpoint="reports/inventory"
          filename="fdr_inventory" extract={(r) => r.report?.entities || []} />
        <ReportButton label="FDR-Q Draft (CMS)" endpoint="reports/fdrq"
          filename="fdrq_draft" extract={(r) => r.report?.rows || []} />
        <ReportButton label="FTEAM Universe (CMS)" endpoint="reports/fteam?months=12"
          filename="fteam_universe" extract={(r) => r.report?.rows || []} />
      </section>

      {/* 1. Summary Stats */}
      <section>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            label="Total Entities"
            value={total_entities}
            gradient="bg-gradient-to-br from-orange-500 to-orange-600"
            icon="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
          />
          <StatCard
            label="Audits in cycle"
            value={inCycle}
            gradient="bg-gradient-to-br from-orange-500 to-amber-600"
            icon="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
          <StatCard
            label="Open Findings"
            value={totalFindings}
            gradient="bg-gradient-to-br from-red-500 to-orange-600"
            icon="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
          <StatCard
            label="Open CAPs"
            value={totalCaps}
            gradient="bg-gradient-to-br from-pink-500 to-orange-500"
            icon="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
          />
        </div>
      </section>

      {/* 2 + 3. Risk Breakdown & Open Findings by Severity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* 2. Risk Breakdown */}
        <section className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-6">
          <SectionHeader title="Risk Breakdown" onViewAll={() => onNavigate('entities')} />
          {risk_breakdown.length === 0 ? (
            <p className="text-sm text-slate-400">No risk data available.</p>
          ) : (
            <div className="space-y-3">
              {['critical', 'high', 'medium', 'low'].map(level => {
                const item = risk_breakdown.find(r => r.risk_level?.toLowerCase() === level);
                if (!item) return null;
                return (
                  <RiskBar key={level} risk_level={level} count={item.count} max={maxRiskCount} />
                );
              })}
            </div>
          )}
        </section>

        {/* 3. Open Findings by Severity */}
        <section className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-6">
          <SectionHeader title="Open Findings by Severity" onViewAll={() => onNavigate('findings')} />
          {open_findings.length === 0 ? (
            <p className="text-sm text-slate-400">No open findings.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {open_findings.map(item => (
                <SeverityBadge key={item.severity} severity={item.severity} count={item.count} />
              ))}
            </div>
          )}
        </section>
      </div>

      {/* 4. The assessment cycle — where every vendor stands, at a glance */}
      <section className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-6">
        <SectionHeader title="Assessment Cycle" onViewAll={() => onNavigate('assessments')} />
        {assessment_cycle.length === 0 ? (
          <p className="text-sm text-slate-400">No active vendors yet.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {CYCLE_STATES.map(([state, label, cls]) => {
              const count = cycleCount(state);
              if (!count) return null;
              return (
                <button key={state} onClick={() => onNavigate('assessments')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full ${cls} hover:ring-1 hover:ring-orange-300 transition-all`}>
                  <span className="text-xs font-semibold">{label}</span>
                  <span className="text-xs font-bold">{count}</span>
                </button>
              );
            })}
          </div>
        )}
        <p className="mt-3 text-xs text-slate-400">Every active vendor, placed by its latest assessment: from not started through notice, vendor response, review, the report round, and the closed score. Click through to work the cycle.</p>
      </section>

      {/* 5. Upcoming Due Items */}
      <section className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-6">
        <SectionHeader title="Upcoming Due Items" />
        {upcoming_due_items.length === 0 ? (
          <p className="text-sm text-slate-400">No upcoming due items.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left pb-2 text-xs font-semibold text-slate-500 uppercase tracking-wide pr-4">Type</th>
                  <th className="text-left pb-2 text-xs font-semibold text-slate-500 uppercase tracking-wide pr-4">Entity</th>
                  <th className="text-left pb-2 text-xs font-semibold text-slate-500 uppercase tracking-wide pr-4">Risk</th>
                  <th className="text-left pb-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">Due Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {upcoming_due_items.map((item, idx) => {
                  const days = daysUntil(item.due_date);
                  const typeLabel = ITEM_TYPE_LABELS[item.item_type] || item.item_type;
                  const riskCfg = RISK_CONFIG[item.risk_level?.toLowerCase()];
                  const rowUrgent = days !== null && days < 0;
                  return (
                    <tr key={idx} className={`transition-colors ${rowUrgent ? 'bg-red-50/40' : 'hover:bg-slate-50/50'}`}>
                      <td className="py-2.5 pr-4">
                        <span className="text-slate-600 font-medium">{typeLabel}</span>
                      </td>
                      <td className="py-2.5 pr-4">
                        <button
                          onClick={() => onOpenEntity(item.entity_id)}
                          className="text-orange-600 font-semibold hover:text-orange-700 hover:underline transition-colors text-left"
                        >
                          {item.entity_name}
                        </button>
                      </td>
                      <td className="py-2.5 pr-4">
                        {riskCfg ? (
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${riskCfg.bg} ${riskCfg.text}`}>
                            {riskCfg.label}
                          </span>
                        ) : (
                          <span className="text-slate-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="py-2.5">
                        <DueDateCell due_date={item.due_date} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

    </div>
  );
}

// ─── CMS-ready report download (CSV) ─────────────────────────────────────────
// Fetches the report endpoint, shows the compliance banner when one applies,
// and downloads the rows as CSV. The export itself is what gets handed to CMS.
function ReportButton({ label, endpoint, filename, extract, banner }) {
  const [busy, setBusy] = React.useState(false);
  const [note, setNote] = React.useState(null);

  const run = async () => {
    setBusy(true); setNote(null);
    try {
      const res = await fetch(`${API}/api/fdr/${endpoint}`, { credentials: 'include' });
      const data = await res.json();
      if (!data.success) { setNote({ err: true, text: data.message || 'Report failed' }); setBusy(false); return; }
      const rows = extract(data) || [];
      if (banner) { const b = banner(data); if (b) setNote({ err: /NOT/.test(b), text: b }); }
      if (rows.length) {
        const cols = Object.keys(rows[0]);
        const esc = (v) => { const s = v === null || v === undefined ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
        const csv = [cols.join(','), ...rows.map(r => cols.map(c => esc(r[c])).join(','))].join('\n');
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
        const a = document.createElement('a');
        a.href = url; a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
        a.click(); URL.revokeObjectURL(url);
      } else if (!banner) {
        setNote({ err: false, text: 'No rows to export yet.' });
      }
    } catch { setNote({ err: true, text: 'Report failed' }); }
    setBusy(false);
  };

  return (
    <div className="flex items-center gap-2">
      {note && (
        <span className={`text-xs px-3 py-1.5 rounded-lg font-medium max-w-md ${note.err ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
          {note.text}
        </span>
      )}
      <button onClick={run} disabled={busy}
        className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold text-slate-600 bg-white border border-slate-200 shadow-sm hover:border-orange-300 hover:text-orange-700 transition-colors disabled:opacity-50">
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
        {busy ? 'Running…' : label}
      </button>
    </div>
  );
}
